// LandingPage — public marketing page shown at / when no authenticated
// session exists. Hero pitch + 'Sign in with Google' CTA + product
// snapshot. Once signed in, /api/auth/me returns 200 and main.tsx
// routes to OnboardingPage instead.

import React, { useEffect, useState } from 'react';

interface AuthConfig { google_oauth: boolean; redirect_uri: string }

export default function LandingPage(): React.ReactElement {
  const [cfg, setCfg] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/auth/config')
      .then(r => r.json())
      .then(setCfg)
      .catch(e => setError(String(e)));
  }, []);

  function signIn(): void {
    window.location.href = '/api/auth/google/start';
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <div className="topbar">
        <div className="brand">
          <span className="brand-mark">DH</span>
          <span className="brand-name">Data Hub<span className="dot">.</span></span>
        </div>
        <div className="topbar-right">
          <span style={{ fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>v0.1.8</span>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '64px 24px' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ink-3)', marginBottom: 12 }}>
          ● Agent-native ingest · per-tenant warehouse · MCP out of the box
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 6vw, 60px)', fontWeight: 600, lineHeight: 1.05, margin: '0 0 18px', color: 'var(--ink)' }}>
          The data plane your AI agents have been missing.
        </h1>
        <p style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--ink-2)', maxWidth: 640, margin: '0 0 28px' }}>
          Real-time webhooks, immutable event log, per-tenant BigQuery, and
          native MCP. Built for agents from day one — not retrofitted from
          ETL. Validate, dedupe, enrich, replay, audit. No data team. No
          batch lag. No setup calls.
        </p>

        {cfg?.google_oauth && (
          <button
            onClick={signIn}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              padding: '12px 22px', fontSize: 15, fontWeight: 500,
              background: 'var(--ink)', color: 'var(--bg)',
              border: 'none', borderRadius: 8, cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M16.5 9.2c0-.6-.05-1.18-.15-1.74H9v3.3h4.2c-.18.97-.73 1.8-1.55 2.36v1.96h2.5c1.46-1.35 2.3-3.34 2.3-5.88z" fill="#4285F4"/>
              <path d="M9 17c2.1 0 3.86-.7 5.15-1.9l-2.5-1.94c-.7.47-1.6.74-2.65.74-2.04 0-3.76-1.38-4.38-3.23H1.94v2.02C3.22 15.27 5.94 17 9 17z" fill="#34A853"/>
              <path d="M4.62 10.67A4.84 4.84 0 0 1 4.36 9c0-.58.1-1.14.26-1.67V5.31H1.94A8 8 0 0 0 1 9c0 1.3.31 2.52.94 3.69l2.68-2.02z" fill="#FBBC05"/>
              <path d="M9 3.58c1.15 0 2.18.4 2.99 1.18l2.22-2.22C13.86.96 12.1.25 9.99.25 6.93.25 4.22 1.98 2.94 4.5l2.68 2.02C6.24 4.96 7.96 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>
        )}
        {cfg && !cfg.google_oauth && (
          <div style={{ padding: '12px 16px', background: 'oklch(0.95 0.06 75)', border: '1px solid oklch(0.78 0.10 75)', borderRadius: 8, color: 'oklch(0.32 0.06 70)', fontSize: 13.5, maxWidth: 600 }}>
            <strong>Login isn't configured yet.</strong> The Google OAuth
            client_id / client_secret env vars are missing on the server.
            Operator with deploy access: set them in Secret Manager + redeploy.
          </div>
        )}
        {error && <div style={{ marginTop: 12, color: 'var(--danger)' }}>{error}</div>}

        <div style={{ marginTop: 64, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
          <Pillar title="5 input channels per source" body="Webhook, REST pull, public upload form, email parse, file upload — all provisioned at create time." />
          <Pillar title="First-class data quality" body="validate_required, validate_dedupe, enrich_fetch triggers. Bad events quarantined; downstream stays clean." />
          <Pillar title="Replay everything ever" body="GCS-durable; reprocess any event by id, range, or saved view filter. Idempotent on event_key." />
          <Pillar title="BigQuery-native" body="promote_to_table MERGEs into a typed BQ table. Per-source dataset isolation; opt-in fan-in." />
          <Pillar title="Operator-grade UI" body="Phase-aware layout (setup → build → monitor). Cmd+K. Toast feedback. Type-to-confirm on every destructive op." />
          <Pillar title="MCP from day one" body="Claude Desktop / Cursor / agents drive the same REST surface. Secrets vault auto-resolves @SECRET refs." />
        </div>

        <div style={{ marginTop: 64, padding: 20, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 8 }}>API + integrations</div>
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            22+ MCP tools · 24+ OpenAPI paths · short bit.ly-style URLs (<code style={{ fontFamily: 'var(--mono)' }}>/h/&#123;slug&#125;</code> · <code style={{ fontFamily: 'var(--mono)' }}>/f/&#123;slug&#125;</code>) · slug rename with 90-day alias history · centralized secrets vault. Built for operators who need it to work and stay out of the way.
          </div>
        </div>
      </div>
    </div>
  );
}

function Pillar({ title, body }: { title: string; body: string }): React.ReactElement {
  return (
    <div style={{ padding: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
